import { EMPTY, from as observableFrom, Observable } from "rxjs";

import { catchError, map, mergeMap, share, skipWhile, take, takeWhile } from "rxjs/operators";
import { cloneDeep } from "lodash";

import { WalletService } from "./wallet.service";

import { Helper } from "@helpers/functions";
import { SocketService } from "../socket.service";
import { InitService } from "@core-services/init.service";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { TradableService } from "../currency/tradable.service";
import { Tradable } from "@models/interfaces/tradable";
import { Stock } from "@models/derivative/currencies/stock";
import { StockWallet } from "@models/derivative/wallets/stock_wallet";
import { EtfWallet } from "@models/derivative/wallets/etf_wallet";
import { Etf } from "@models/derivative/currencies/etf";
import { Index } from "@models/asset/index/index.model";
import { IndexWallet } from "@models/asset/index/index_wallet";
import { MetalWallet } from "@models/asset/metal/metal_wallet";
import { Metal } from "@models/asset/metal/metal";
import { v4 as uuid } from "uuid";
import { convertTradableToFiat } from "@helpers/tradable-convert";
import { TradableTransaction } from "@models/transaction";
import { GetPendingTradesGQLService, TradingOperationType } from "@gql/offer";
import { ApicallOption } from "@core-services/apicall.service";
import { DestructorService } from "@core-services/destructor.service";
import { Cryptocoin, CryptoWallet, Fiat, Trade } from "@models";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Etc } from "@models/derivative/currencies/etc";
import { EtcWallet } from "@models/derivative/wallets/etc_wallet";
import { LeveragedToken } from "@models/asset/cryptocoins/currencies/leveraged_tokens";
import { LeveragedTokenWallet } from "@models/asset/cryptocoins/wallets/leveraged_token_wallet";
import { WalletsBalancesFacadeService } from "@modules/+new/core/wallets-balances/domain/+state/wallets-balances.facade.service";
import { inject } from "@angular/core";
import { SecurityToken } from "@models/asset/cryptocoins/currencies/security_token";
import { SecurityTokenWallet } from "@models/asset/cryptocoins/wallets/security_token_wallet";

@UntilDestroy()
export abstract class TradableWalletService<
	TW extends TradableWallet,
	TT extends TradableTransaction,
	T extends Tradable
> extends WalletService<TW, TT> {
	protected _wallet: TW = null;

	protected abstract socketService: SocketService;

	public abstract TxType: typeof TradableTransaction;
	public abstract WalletType: typeof TradableWallet;
	protected abstract currencyService: TradableService<T>;

	protected abstract transactionSocketRoomName: string;
	protected abstract walletSocketRoomName: string;
	private walletsBalancesFacade = inject(WalletsBalancesFacadeService);

	protected constructor(
		protected initService: InitService,
		protected destructor: DestructorService,
		public name: string,
		protected getPendingTradesGQLService: GetPendingTradesGQLService
	) {
		super(initService, destructor, name);

		this.initService
			.getInitiatedServicesAsObservable()
			.pipe(takeWhile((initiated: string[]) => !initiated.includes(this.name)))
			.subscribe({
				complete: () => {
					this.currencyService.startSockets();
				}
			});
	}

	public socketInit(): void {
		this.socketService
			.joinRoom<TT>({
				name: this.transactionSocketRoomName,
				type: this.TxType,
				isPrivate: true
			})
			.pipe(untilDestroyed(this))
			.subscribe((transaction) => {
				this.updateReceived(transaction);
				this.transactionNotifyService.show("new", transaction);
			});

		this.socketService
			.joinRoom<TW>({
				name: this.walletSocketRoomName,
				type: this.WalletType,
				isPrivate: true
			})
			.pipe(untilDestroyed(this))
			.subscribe((wallet) => {
				//EATR-1141 but fix default wallet is set to false from WS
				const updateWallet = this.checkIfSelectedDefaultIsFalse(wallet);
				this.upsert(updateWallet);
				this.walletsBalancesFacade.refreshBalances$().pipe(take(1)).subscribe();
			});
	}

	public upsert(m: TW): void {
		this.hasItem(m) ? this.updateItem(m) : this.replaceItem(m);
	}

	public replaceItem(m: TW): void {
		const foundWallet = this.list.find((wallet) => m.getCurrencyId() === wallet.getCurrencyId());
		if (foundWallet) {
			if (foundWallet.getId().includes("generated")) {
				this.list = [...this.list.filter((wallet) => wallet.getId() !== foundWallet.getId()), m];
			}
		}
	}

	private checkIfSelectedDefaultIsFalse(wallet: TW): TW {
		// create deep copy of wallet object
		const updatedWallet = cloneDeep(wallet);

		// get list of current wallets
		const wallets = this._list.getValue();

		//check if incoming and current wallet match then check current default is true
		const walletFromList = wallets.find((wallet) => wallet.getId() === updatedWallet.getId());
		if (!walletFromList) {
			//it means that the user didn't have that type of wallet so we must set it to default
			updatedWallet.setDefault(true);
		}
		if (walletFromList) {
			updatedWallet.setDefault(walletFromList.isDefault());
		}

		// return updated wallet
		return updatedWallet;
	}

	public getTransactions(): Observable<TT[]> {
		return this.apicallService.get("wallets/transactions");
	}

	public getTransaction(walletId: string, transactionId: string): Observable<TT> {
		return this.apicallService
			.get(`wallet/${walletId}/transaction/${transactionId}`, new ApicallOption(this.TxType))
			.pipe(map((res: TT[]) => res[0]));
	}

	public updateWalletBalance(transaction: TT): void {
		this.apicallService
			.get("wallet/" + transaction.getWalletId(), new ApicallOption(this.WalletType))
			.subscribe((a) => {
				this.wallet = a[0];
				this.updateItem(this.wallet);
			});
	}

	public get list(): TW[] {
		return this._list.getValue().filter((c) => !c.isDeleted());
	}

	public set list(w: TW[]) {
		this._list.next(w);
		if (!this.initService.didInit([this.name])) {
			this.initWallets();
			this.initService.init(this.name);
		}
	}

	public updateItem(m: TW, id: string = m.getId()): void {
		this.list = this._list.getValue().map((item) => (item.getId() === id ? m : item));
	}

	// TODO: eslint fix
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	public abstract getParseData(wallets: any): TW[];

	public initWallets(): void {
		this.currencyService
			.getList()
			.pipe(
				skipWhile((t) => t.length === 0), // Eliminate unnecessary premature calls to subscribe(), since list might not yet be populated
				map((tradables: Tradable[]) => {
					let wallets: TradableWallet[] = this.list;
					tradables.map((t) => {
						wallets = wallets.concat(this.createMissingWallets(t));
					});
					return wallets;
				}),
				take(1)
			)
			.subscribe((tradableWallets: TW[]) => {
				this._list.next(tradableWallets);
			});
	}

	public fetchWallets(): void {
		this.apicallService.get("asset-wallets").subscribe(
			(data) => {
				const list = this.getParseData(data);
				this.updateItems(list);
				this.addItems(list);
				this._loaded = true;
				this.initService.init(this.name);
			},
			(error) => Helper.handleError(error)
		);
	}

	public getSumOfAssetInFiat(asset: T, fiat?: Fiat): number {
		const sum = this.list
			.filter((wallet: TW) => asset.getId() === wallet.getCurrencyId())
			.map((wallet: TW) => wallet.getBalance())
			.reduce((sum: number, balance: number) => sum + balance, 0);
		return convertTradableToFiat(asset, sum, fiat);
	}

	public getSumOfWalletsInFiat(fiat?: Fiat): number {
		return this.getAssetsFromWallet()
			.map((asset: T) => this.getSumOfAssetInFiat(asset, fiat))
			.reduce((sum: number, balance: number) => sum + balance, 0);
	}

	public getNotEmptyCryptoCoins(): T[] {
		return this.getAssetsFromWallet().filter((asset: T) => this.getBalanceFromAsset(asset) > 0);
	}

	public getAssetsFromWallet(): T[] {
		return this.list
			.map((wallet: TW) => this.currencyService.getItemById(wallet.getCurrencyId()))
			.filter((asset: T, index, self) => !!asset && index === self.indexOf(asset));
	}

	public getBalanceFromAsset(asset: T): number {
		return this.list
			.filter((wallet: TW) => wallet.getCurrencyId() === asset.getId())
			.map((wallet: TW) => wallet.getBalance())
			.reduce((sum: number, balance: number) => sum + balance, 0);
	}

	public get defaultWallet(): TW {
		return this.list.find((a) => a.isDefault());
	}

	public updateTx(transaction: TT): void {
		this.apicallService
			.put(
				"wallet/" + transaction.getWalletId() + "/transaction/" + transaction.getId(),
				new ApicallOption(this.TxType, transaction)
			)
			.subscribe(
				(transactionNew) => {
					transaction = transactionNew[0];
					this.infoService.setSuccess(this.translationService.translate("info_text_transaction_updated"));
					this.transactionNotifyService.show("update", transaction);
				},
				(error) => Helper.handleError(error)
			);
	}

	public getWalletsByTradableId(id: string): TW[] {
		return this.list.filter((w) => w.getCurrencyId() === id) || [];
	}

	public findByTrade(t: Trade): TW {
		return this.list.find((w) => w.getId() === t.getWalletId());
	}

	public get wallet(): TW {
		return this._wallet;
	}

	public set wallet(w: TW) {
		this._wallet = w;
	}

	public getCurrencyType(): string {
		return this.getAssetsFromWallet()[0].getCurrencyType();
	}

	public cancelById(walletId: string, transactionId: string): Observable<TT> {
		return this.getTransaction(walletId, transactionId).pipe(mergeMap((tx) => observableFrom(this.cancel(tx))));
	}

	public deleteWallet(wallet: TW): Observable<TW> {
		return this.apicallService.deleteRequest(`wallet/${wallet.getId()}`, new ApicallOption(this.WalletType)).pipe(
			map((res) => {
				this.upsert(res[0]);
				return res[0];
			}),
			share()
		);
	}

	public setName(wallet: TW): Observable<TW> {
		return this.apicallService
			.put(
				`wallet/${wallet.getId()}`,
				new ApicallOption(this.WalletType, {
					name: wallet.getName()
				})
			)
			.pipe(
				map((res: TW[]) => {
					this.updateItem(res[0]);
					wallet.dirty = false;
					return res[0];
				})
			);
	}

	public createNewWallet(wallet: TW): Observable<TW[]> {
		return this.apicallService.post("wallets", new ApicallOption(this.WalletType, wallet));
	}

	public getItemById(id: string, deleted = false): TW {
		if (!deleted) {
			return super.getItemById(id);
		}
		return this._list.getValue().find((w) => w.getId() === id);
	}

	public getPendingTradesByAssetId(assetId: string): Observable<TradingOperationType[]> {
		return this.getPendingTradesGQLService.watch({ assetId }, { fetchPolicy: "network-only" }).valueChanges.pipe(
			map(({ data }) =>
				data.tradingOperationsInProgress.affectedAssets.length
					? data.tradingOperationsInProgress.affectedAssets[0].operations.map((d) => d.type)
					: []
			),
			catchError(() => EMPTY)
		);
	}

	private createMissingWallets(tradable: Tradable): TW[] {
		const hasWallet: TW[] = this.list.filter((w) => w.getCurrencyId() === tradable.getId());
		if (hasWallet.length > 0) {
			// We don't need to return any wallets here since this function should only return the autogenerated ones
			return [];
		}

		if (!!tradable && !tradable.isIndexOnly()) {
			let w = null;
			if (tradable instanceof Stock && this.WalletType === StockWallet) {
				w = new StockWallet();
			}
			if (tradable instanceof Etf && this.WalletType === EtfWallet) {
				w = new EtfWallet();
			}
			if (tradable instanceof Etc && this.WalletType === EtcWallet) {
				w = new EtcWallet();
			}
			if (tradable instanceof Index && this.WalletType === IndexWallet) {
				w = new IndexWallet();
			}
			if (tradable instanceof Metal && this.WalletType === MetalWallet) {
				w = new MetalWallet();
			}
			if (tradable instanceof Cryptocoin && this.WalletType === CryptoWallet) {
				w = new CryptoWallet();
			}
			if (tradable instanceof LeveragedToken && this.WalletType === LeveragedTokenWallet) {
				w = new LeveragedTokenWallet();
			}

			if (tradable instanceof SecurityToken && this.WalletType === SecurityTokenWallet) {
				w = new SecurityTokenWallet();
			}

			if (w) {
				w.setCurrencyId(tradable.getId());
				w.setDefault(true);
				w.setNew(true);
				const walletId =
					"generated_" +
					tradable.constructor.name.toLowerCase() +
					"_" +
					tradable.getSymbol(true) +
					"_" +
					uuid();
				w.setId(walletId);
				return [w];
			}
		}

		return [];
	}
}
